/**
 * GitHub sync routes — org-scoped (not sub-tenant-scoped), so they take an
 * `orgId` argument at every call site instead of reading the subtenant
 * context from the shared client. They're also wrapped in a
 * `{ success, data|error }` envelope on the server; this module unwraps
 * it to match the shape the UI expected before the @configural/platform
 * migration.
 */

import { ApiError, createClient } from "@configural/platform/api-client";

const BASE = "/api/v1/github";

// Minimal per-call client — no persistent header provider, since `orgId`
// varies per request.
const client = createClient({ baseUrl: BASE, credentials: "include" });

/**
 * Issue a GitHub request, tagging the `orgId` as both a header and a
 * query param (the server checks either). Unwraps the success envelope.
 *
 * @param {"GET"|"POST"|"PUT"|"DELETE"} method
 * @param {string} path — starts with "/"
 * @param {string} orgId
 * @param {unknown} [body]
 */
async function req(method, path, orgId, body) {
  try {
    const data = await client.request(method, path, {
      body,
      query: { orgId },
      headers: { "x-org-id": orgId },
    });
    // Server envelope: `{ success: true, ...payload }` or
    // `{ success: false, error }`. Passing non-JSON (old server running
    // on the wrong port) surfaces as `data === null` — translate that
    // to the legacy error message.
    if (data === null) {
      throw new Error(
        `Server returned non-JSON. Is the server running on port 3005?`
      );
    }
    return data;
  } catch (err) {
    if (err instanceof ApiError) {
      const msg =
        (err.body && typeof err.body === "object" && err.body.error) ||
        err.message;
      throw new Error(msg);
    }
    throw err;
  }
}

export const getConfig = (orgId) => req("GET", "/config", orgId);
export const saveConfig = (orgId, body) => req("PUT", "/config", orgId, body);
export const testConn = (orgId, body) => req("POST", "/test-connection", orgId, body);
export const createGHRepo = (orgId, body) => req("POST", "/create-repo", orgId, body);
export const push = (orgId, body) => req("POST", "/push", orgId, body);
export const getPushStatus = (orgId) => req("GET", "/push-status", orgId);
export const pull = (orgId) => req("POST", "/pull", orgId);
export const getPullLog = (orgId) => req("GET", "/pull-log", orgId);
export const getStatus = (orgId) => req("GET", "/status", orgId);
